import { useEffect, useState } from 'react'

interface InViewOptions {
    once?: boolean
    threshold?: number
    rootMargin?: string
}

/**
 * Tells whether an element is on screen. Returns a callback ref and the flag.
 * Starts as false on the server and on the first client render, so markup always matches.
 */
export const useInView = ({
    once = true,
    threshold = 0.2,
    rootMargin = '0px 0px -8% 0px'
}: InViewOptions = {}): readonly [(node: Element | null) => void, boolean] => {
    const [node, setNode] = useState<Element | null>(null)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        if (!node) return

        if (typeof IntersectionObserver === 'undefined') {
            setInView(true)
            return
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry?.isIntersecting) {
                setInView(true)

                if (once) observer.disconnect()
            } else if (!once) {
                setInView(false)
            }
        }, { threshold, rootMargin })

        observer.observe(node)

        return () => observer.disconnect()
    }, [node, once, threshold, rootMargin])

    return [setNode, inView] as const
}
